import { Injectable } from '@angular/core';
import { AppConfig } from 'common';
import { UvExplorerClient } from 'common';
import { Observable, Subject } from 'rxjs/Rx';
import { FindHostResultDto, PatchManagerRequestType, PatchManagerResultDto, PrtgConnectResultDto, CopyProtocolsResultDto } from 'UvExplorerServer.Dtos.TS/Tools';
import { ValueEncoder } from 'DiscoveryEngine.TS/Utils/ValueEncoder';
import { IGetPortStateResultDto, ISetPortStateResultDto, PortStateDto, PortStateType } from 'UvExplorerServer.Dtos.TS/Session';

@Injectable()
export class ToolsWebService {
	constructor(private appConfig: AppConfig, private uvExplorerClient: UvExplorerClient) {
		return;
	}

	public processPatchManagerRequest(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, requestType: PatchManagerRequestType,
		campusName: string, buildingName: string, jackName: string, deviceGuid: string, roomName: string, doLive: boolean): Observable<PatchManagerResultDto> {

		let url = this.appConfig.webServerUrl + `api/session/patchmanager`;
		let subject = new Subject<PatchManagerResultDto>();

		let body = {
			patch_manager_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				request_type: PatchManagerRequestType[requestType],
				campus_name: campusName,
				building_name: buildingName,
				jack_name: jackName,
				device_guid: deviceGuid,
				room_name: roomName,
				do_live: ValueEncoder.EncodeBoolean(doLive),
			}
		};

		this.uvExplorerClient.post<PatchManagerResultDto>(url, body).subscribe(
			(result: PatchManagerResultDto) => {
				subject.next(result);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public processFindHostRequest(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, dnsHostname: string, hostIpAddress: string, hostMacAddress: string, doLive: boolean):
		Observable<FindHostResultDto> {

		let url = this.appConfig.webServerUrl + `api/session/findhost`;
		let subject = new Subject<FindHostResultDto>();

		let body = {
			find_host_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				dns_hostname: dnsHostname,
				host_ipaddress: hostIpAddress,
				host_macaddress: hostMacAddress,
				do_live: ValueEncoder.EncodeBoolean(doLive),
			}
		};

		this.uvExplorerClient.post<FindHostResultDto>(url, body).subscribe(
			(result: FindHostResultDto) => {
				subject.next(result);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public getPortState(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, stateTypes: PortStateType[],
		deviceGuids: string[], ifIndexes: number[]) : Observable<IGetPortStateResultDto> {

		let body = {
			get_port_state_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				state_types: [],
				device_guids: [],
				if_indexes: [],
			}
		};
		stateTypes.forEach((type: PortStateType) => {
			body.get_port_state_request.state_types.push({ value: PortStateType[type] });
		});
		deviceGuids.forEach((guid: string) => {
			body.get_port_state_request.device_guids.push({ value: guid });
		});
		ifIndexes.forEach((ifIndex: number) => {
			body.get_port_state_request.if_indexes.push({ value: "" + ifIndex });
		});

		let url = this.appConfig.sessionServiceUrl + `api/session/getportstate`;
		return this.uvExplorerClient.post<IGetPortStateResultDto>(url, body);
	}

	public setPortState(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, stateType: PortStateType, entries: PortStateDto[])
		: Observable<ISetPortStateResultDto> {

		let body = {
			set_port_state_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				state_type: PortStateType[stateType],
				entries: [],
			}
		};
		entries.forEach((entry: PortStateDto) => {
			let obj = {};
			entry.Write(obj, {});

			body.set_port_state_request.entries.push(obj);
		});

		let url = this.appConfig.sessionServiceUrl + `api/session/setportstate`;
		return this.uvExplorerClient.post<ISetPortStateResultDto>(url, body);
	}

	public testPrtgConnect(prtgServerURL: string, prtgUsername: string, prtgPasshash: string, prtgApiKey: string):
		Observable<PrtgConnectResultDto> {

		let url = this.appConfig.webServerUrl + `api/session/prtgconnect`;
		let subject = new Subject<PrtgConnectResultDto>();

		let body = {
			prtg_connect_request: {
				server_url: prtgServerURL,
				user: prtgUsername,
				passhash: prtgPasshash,
				api_key: prtgApiKey,
			}
		};

		this.uvExplorerClient.post<PrtgConnectResultDto>(url, body).subscribe(
			(result: PrtgConnectResultDto) => {
				subject.next(result);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public processCopyNetworkProtocols(sessionGuid: string, sourceNetwork: string, targetNetwork: string, copyAll: boolean): Observable<CopyProtocolsResultDto> {

		let url = this.appConfig.webServerUrl + `api/session/copyprotocols`;
		let subject = new Subject<CopyProtocolsResultDto>();

		let body = {
			copy_protocols_request: {
				source_network: sourceNetwork,
				target_network: targetNetwork,
				copy_all: ValueEncoder.EncodeBoolean(copyAll),
			}
		};

		this.uvExplorerClient.post<CopyProtocolsResultDto>(url, body).subscribe(
			(result: CopyProtocolsResultDto) => {
				subject.next(result);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}


}
